import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Copy, Pencil, Trash2, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  listCustomIngredients,
  saveCustomIngredient,
  deleteCustomIngredient,
} from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import LibraryEntryDialog from "../components/LibraryEntryDialog";

export const LibraryPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit" | "duplicate"
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listCustomIngredients();
      setEntries(data || []);
    } catch {
      toast.error("Erreur de chargement de la bibliothèque.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.name_fr.toLowerCase().includes(q));
  }, [entries, search]);

  const handleSave = async (payload) => {
    try {
      await saveCustomIngredient(payload);
      toast.success(
        dialogMode === "duplicate" ? "Variante créée." : "Ingrédient enregistré."
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement.");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteCustomIngredient(confirmDelete.id);
      toast.success("Ingrédient supprimé.");
      setEntries((prev) => prev.filter((x) => x.id !== confirmDelete.id));
    } catch {
      toast.error("Erreur de suppression.");
    } finally {
      setConfirmDelete(null);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setDialogMode("create");
  };
  const openEdit = (entry) => {
    setEditTarget(entry);
    setDialogMode("edit");
  };
  const openDuplicate = (entry) => {
    setEditTarget(entry);
    setDialogMode("duplicate");
  };
  const closeDialog = (open) => {
    if (!open) {
      setDialogMode(null);
      setEditTarget(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12" data-testid="library-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-700 mb-2">
            <BookOpen className="h-3 w-3 inline -mt-0.5 mr-1" />
            Catalogue interne
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
            Ma bibliothèque
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Vos ingrédients personnalisés réutilisables d'une recette à l'autre.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg h-10 px-5"
          data-testid="library-create-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvel ingrédient
        </Button>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un ingrédient…"
          className="pl-11 h-11 bg-white border-stone-200 focus-visible:ring-emerald-500"
          data-testid="library-search-input"
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div
          className="border-2 border-dashed border-stone-200 rounded-2xl p-12 text-center bg-stone-50/60"
          data-testid="library-empty"
        >
          <p className="text-stone-500 text-sm">
            Aucun ingrédient personnalisé pour l'instant.
          </p>
          <p className="text-stone-400 text-xs mt-1">
            Créez-en un depuis une recette (bouton « Ingrédient personnalisé ») ou directement ici.
          </p>
        </div>
      )}

      {!loading && entries.length > 0 && filtered.length === 0 && (
        <div className="text-sm text-stone-500 py-8 text-center" data-testid="library-no-match">
          Aucun résultat pour « {search} ».
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid gap-3" data-testid="library-grid">
          {filtered.map((e) => (
            <div
              key={e.id}
              className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5 hover:border-emerald-300 transition-colors flex items-center justify-between gap-4 flex-wrap"
              data-testid={`library-row-${e.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">{e.name_fr}</div>
                <div className="text-xs text-stone-500 mt-1 tabular-nums">
                  <span className="font-bold text-emerald-700">
                    {e.nutrients_per_100g.calories} kcal
                  </span>
                  <span className="mx-1.5">·</span>
                  P {e.nutrients_per_100g.protein}g · G {e.nutrients_per_100g.carbs}g · L{" "}
                  {e.nutrients_per_100g.fat}g · F {e.nutrients_per_100g.fiber}g
                  <span className="text-stone-400 ml-1">/ 100 g</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(e)}
                  className="text-stone-600 hover:text-emerald-700 hover:bg-emerald-50"
                  data-testid={`library-edit-${e.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openDuplicate(e)}
                  className="text-stone-600 hover:text-emerald-700 hover:bg-emerald-50"
                  data-testid={`library-duplicate-${e.id}`}
                >
                  <Copy className="h-4 w-4 mr-1.5" />
                  Dupliquer
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfirmDelete(e)}
                  className="text-stone-400 hover:text-red-600 hover:bg-red-50 h-9 w-9"
                  data-testid={`library-delete-${e.id}`}
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <LibraryEntryDialog
        open={!!dialogMode}
        onOpenChange={closeDialog}
        mode={dialogMode}
        entry={editTarget}
        onSave={handleSave}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet ingrédient ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {confirmDelete?.name_fr} » sera retiré de votre bibliothèque. Les recettes
              existantes qui l'utilisent ne sont pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-library-delete"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LibraryPage;
